import React from 'react'
import { connect } from 'react-redux'
import { Navigate } from 'react-router-dom'

import { Map } from 'immutable'

import {
  COMMON_MIXPANEL_PROPERTIES,
  CustomerPropertiesMap,
  Episode,
  pathwaysRedirectToTriage,
  useFlags,
} from '@lyrahealth-inc/shared-app-logic'
import { toJS, useFetcher } from '@lyrahealth-inc/ui-core-crossplatform'
import { LoadingIndicator } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons'

import { REGISTRATION_SUCCESS_PATH } from '../../common/constants/registrationFormConstants'
import { REDIRECT_URL_KEY } from '../../common/constants/routingConstants'
import { useActiveMicrositesMetadataList } from '../../common/hooks/useActiveMicrositesMetadataList'
import { updateRedirectPathForPathwayOptions } from '../../common/utils/pathwaysUtils'
import { addFromUrlRedirectParam } from '../../common/utils/routingUtils'
import { hasRole } from '../../common/utils/userUtils'
import { getCustomerPropertiesMap } from '../../data/customer/customerSelectors'
import { getForkedTriageV2Enabled } from '../../data/featureFlags/featureFlagSelectors'
import mixpanel from '../../data/mixpanel/mixpanel-browser'
import { getIsUserInternational, getIsUserTeen } from '../../data/user/userSelectors'
import { getEpisodes } from '../lyraTherapy/data/LyraTherapyActions'
import { getHasFetchedDashboard } from '../lyraTherapy/data/LyraTherapySelectors'
import { clearSelectedPathwayOption } from '../register/data/registerActions'

type IndexRedirectorProps = {
  episodes?: Episode[]
  user?: $TSFixMe
  isTeenUser?: boolean
  customerPropertiesMap?: CustomerPropertiesMap
  isInternationalUser?: boolean
  forkedTriageV2Enabled?: boolean
  dashboardFetched?: boolean
  clearSelectedPathwayOption: () => void
  getEpisodes: (params: { lyraId: string }) => void
}

const IndexRedirector = ({
  episodes,
  user,
  isTeenUser,
  isInternationalUser,
  customerPropertiesMap,
  forkedTriageV2Enabled,
  dashboardFetched,
  clearSelectedPathwayOption,
  getEpisodes,
}: IndexRedirectorProps) => {
  const { is1LyraEnabledForCareRoutes } = useFlags()
  const activeMicrositesMetadataList = useActiveMicrositesMetadataList()

  // Only fetch episodes if they haven't been fetched yet by MemberSecureAppContainer
  // This avoids duplicate fetches while ensuring episodes are loaded before making redirect decisions
  const [loadingEpisodes, , hasFetchedEpisodes] = useFetcher(
    [[getEpisodes, { lyraId: user?.id }, !!user?.id && !dashboardFetched]],
    [user?.id, dashboardFetched],
  )

  if (!user?.id || loadingEpisodes || (!hasFetchedEpisodes && !dashboardFetched)) {
    return <LoadingIndicator />
  }

  if (window.sessionStorage.getItem(REDIRECT_URL_KEY)) {
    // If there is a redirect URL in session storage, direct the user there
    let redirectUrl = JSON.parse(window.sessionStorage.getItem(REDIRECT_URL_KEY) || '')
    const updateStorage = (redirectUrl: string) => {
      window.sessionStorage.setItem(REDIRECT_URL_KEY, JSON.stringify(redirectUrl))
    }
    redirectUrl = updateRedirectPathForPathwayOptions({
      redirectUrl,
      isTeenUser: isTeenUser ?? false,
      isInternationalUser: isInternationalUser ?? false,
      customerPropertiesMap,
      forkedTriageV2Enabled: forkedTriageV2Enabled ?? false,
      updateStorage,
    })

    const redirectToTriage = pathwaysRedirectToTriage(redirectUrl)
    clearSelectedPathwayOption()
    if (redirectToTriage) {
      // we cannot directly navigate to self or child triage from this point,
      // so we navigate to HomeBaseContainer
      // which handles redirecting to those paths
      return <Navigate to={REGISTRATION_SUCCESS_PATH} />
    } else {
      /** @todo Refactor how we set entry point. This is a hack for microsite toolkits */
      for (const site of activeMicrositesMetadataList) {
        const mixpanelUtmCampaign = mixpanel.get_property('utm_campaign')
        if (mixpanelUtmCampaign != undefined && mixpanelUtmCampaign === site.trackUTMParams?.utm_campaign) {
          const [redirectPath, redirectQueryParams] = redirectUrl.split('?')
          const params = new URLSearchParams(redirectQueryParams)
          params.set(COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT, site.page)
          redirectUrl = `${redirectPath}?${params.toString()}`
          break
        }
      }
      redirectUrl = addFromUrlRedirectParam(redirectUrl)
      window.sessionStorage.removeItem(REDIRECT_URL_KEY)
      return <Navigate to={redirectUrl} />
    }
  } else if (hasRole(user, 'ROLE_care_manager')) {
    return <Navigate to='/secure/care-manager/provider-matcher' />
  } else if (episodes?.length) {
    // If the client is seeing an LT provider, redirect them to the assignments page
    return <Navigate to={is1LyraEnabledForCareRoutes ? '/secure/assignments' : '/secure/index/assignments'} />
  } else {
    return <Navigate to='/secure/index/search-care' />
  }
}

const mapStateToProps = ($$state: $TSFixMe) => ({
  episodes: $$state.getIn(['lyraTherapy', 'episodes'], Map()),
  user: $$state.get('user'),
  isTeenUser: getIsUserTeen($$state),
  isInternationalUser: getIsUserInternational($$state),
  forkedTriageV2Enabled: getForkedTriageV2Enabled($$state),
  customerPropertiesMap: getCustomerPropertiesMap($$state),
  dashboardFetched: getHasFetchedDashboard($$state),
})

export default connect(mapStateToProps, { clearSelectedPathwayOption, getEpisodes })(toJS(IndexRedirector))
